import useToast from '@hooks/ui/useToast';
import { DiscountCouponRow } from '@models/discount';
import { OrderDiscount, OrderLine } from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { useDispatch } from 'react-redux';

function useCouponControl() {
    const state = useCalculatedRequest();
    const { orderLines, totalAmount } = state;
    const dispatch = useDispatch();
    const toast = useToast();

    const removeCoupon = (couponId: string) => {
        dispatch(orderActions.removeCoupon(couponId));
    };

    const getDiscountAmount = (
        coupon: DiscountCouponRow,
        item?: OrderLine,
    ) :number => {
        // 상품(ITEM) 쿠폰: 특정 상품 1줄에 대한 할인 계산
        if (item) {
            const options = [...item.defaultOptions, ...item.personalOptions]
                .map((option) => option.options)
                .flat();
            const optionPrice = options
                .map((option) => option.quantity * option.optionPrice)
                .reduce((a, b) => a + b, 0);
            // 추가 옵션(양수)은 쿠폰 할인 기준 가격에서 제외하고,
            // 선택 해제 옵션(음수)만 반영하여 기준가를 낮춤
            const totalPrice = item.itemPrice + Math.min(optionPrice, 0);

            if (coupon.discountMethod === 'RATE') {
                return Math.floor((totalPrice * (coupon.discountValue! / 100)));
            }
            // BOGO(1+1): 해당 상품 1개 금액 전액을 할인으로 처리
            if (coupon.discountMethod === 'BOGO') {
                return totalPrice;
            }
            // FIXED: 정액 할인이 상품 금액을 초과하지 않도록 cap
            return Math.min(totalPrice, coupon.discountValue!);
        }

        // 주문(ORDER) 쿠폰: 장바구니 전체 금액에 대한 할인 계산
        // discounts를 비워서 다른 할인 적용 전 순수 상품 합계를 기준으로 계산
        const calculatedTotal = orderWithTotalAmount({
            ...state, discounts: [],
        }).totalAmount;
        if (coupon.discountMethod === 'RATE') {
            const discountOrder = Math.floor((calculatedTotal * (coupon.discountValue! / 100)));
            // 비율 할인은 maxDiscountAmount로 상한을 제한
            return Math.min(coupon.maxDiscountAmount!, discountOrder);
        }
        // FIXED: 정액 할인이 총 주문금액을 초과하지 않도록 cap
        return Math.min(coupon.discountValue!, calculatedTotal);
    };

    // 쿠폰 적용 대상 상품+옵션 조합을 서버 전송용 고유 키로 변환
    const makeItemKey = (
        orderLine?:OrderLine,
        item?:OrderLine,
    ) => {
        const options = orderLine
            ? [...orderLine.defaultOptions, ...orderLine.personalOptions]
                .map((it) => it.options)
                .flat() : undefined;
        // 옵션 순서가 클라이언트와 서버 간에 다를 수 있으므로 정렬하여 동일 키 보장
        const optionKey = options
            ?.map((it) => `${it.posId}x${it.quantity}`)
            .sort()
            .join(':');

        return [item?.posId, optionKey]
            .filter((it) => !!it)
            .join(':');
    };

    const toggleDiscountCoupon = (coupon: DiscountCouponRow, remove: boolean, orderLine?: OrderLine) => {
        // 동일 쿠폰이 중복 등록되지 않도록 먼저 제거한 뒤 필요 시 재등록하는 패턴
        removeCoupon(coupon.id);
        if (remove) {
            // remove=true이면 체크 해제 요청이므로 제거만 하고 종료
            return;
        }
        const newDiscount: OrderDiscount[] = [];
        const item = orderLines
            .find((line) => line.shopItemId === orderLine?.shopItemId)!;
        const discountAmount = getDiscountAmount(coupon, item);

        if (coupon.minOrderAmount && coupon.minOrderAmount > totalAmount) {
            console.log({ totalAmount });
            toast.error(`최소 주문 금액 ${coupon.minOrderAmount}원 이상일 때 사용 가능합니다.`);
            return;
        }
        const options = orderLine
            ? [...orderLine.defaultOptions, ...orderLine.personalOptions]
                .map((it) => it.options)
                .flat() : undefined;

        // isSelected 비교에서 옵션 조합으로 체크 상태를 구분하기 위해 shopOptionId 목록 저장
        const optionIds = options?.map((it) => it.shopOptionId);
        const itemKey = makeItemKey(orderLine, item);

        newDiscount.push({
            type: 'COUPON',
            couponId: coupon.id,
            couponName: coupon.title,
            // orderLine이 있으면 상품 단위 할인, 없으면 주문 전체 할인
            discountScope: orderLine?.shopItemId ? 'ITEM' : 'ORDER',
            discountAmount,
            itemId: orderLine?.shopItemId,
            itemName: item?.itemName,
            optionIds,
            // itemKey는 ITEM 쿠폰에서만 전송 (ORDER 쿠폰은 불필요)
            itemKey: coupon.discountTarget === 'ITEM' ? itemKey : undefined,
            discountMethod: coupon.discountMethod,
        });

        dispatch(orderActions.setDiscounts(newDiscount));
    };

    const clearDiscountCoupon = () => {
        dispatch(orderActions.clearCoupons());
    };

    return {
        toggleDiscountCoupon,
        clearDiscountCoupon,
    };
}

export default useCouponControl;
